import { type ParticipantMuteType, isInsuranceParticipant } from "@/services/caseNotificationsApi";

/*
  Stummschaltung je Beteiligtem schon bei der Anlage
  (Kundenfeedback 27.09.2026, III):

    "Bei Anlage der Beteiligten konnte ich nicht auswählen, dass einzelne
     keine Benachrichtigungen erhalten! Das müssen wir schon bei der Anlage
     abfragen und einstellen können, nicht erst im Nachgang."

  Der Dialog "Beteiligte zuordnen" trägt seit diesem Feedback je Auswahlzeile
  einen Schalter "E-Mail-Benachrichtigungen" (Standard: an). Dieses Modul
  übersetzt den Schalterzustand nach erfolgreicher Anlage in die Aufrufe von
  PUT /protected/cases/{id}/notifications/participants/{type}/{id}.

  Reine Funktionen ohne Vue- oder Netzwerkabhängigkeit, damit sich alle
  Fälle (Abteilung, Externer, Versicherung, Einladung mit/ohne Session-ID)
  isoliert testen lassen.
*/

/** Art der Auswahlzeile im Dialog "Beteiligte zuordnen". */
export type SelectionKind = "department" | "central" | "contact" | "insurance" | "invitation";

/** Eine Auswahlzeile mitsamt Schalterzustand. */
export interface NotifySelection {
  type: SelectionKind;
  /** true = erhält E-Mail-Benachrichtigungen (Standard), false = stumm. */
  notify: boolean;
  /** Rolle im Fall; "Versicherung" sperrt die Zeile systemseitig. */
  role?: string | null;
  /** Anzeigename – wandert als Snapshot in die Historie. */
  name?: string;
  /** Empfängeradresse – der Versandpfad kennt oft nur die Adresse. */
  email?: string;
}

/**
 * Ergebnis der Anlage einer Auswahlzeile: Welche Beteiligten-ID hat der
 * Server vergeben? Abteilungen behalten die gesendete Abteilungs-ID, externe
 * Beteiligte tragen die `id` aus der 201-Antwort, E-Mail-Einladungen die ID
 * der Registrierungs-Session (aus der Beteiligten-Liste, Status "pending").
 * null/undefined = unbekannt; die Zeile wird dann übersprungen.
 */
export interface CreatedParticipant {
  selection: NotifySelection;
  idParticipant?: number | null;
}

/** Ein auszuführender Mute-Aufruf. */
export interface ParticipantMuteRequest {
  participantType: ParticipantMuteType;
  idParticipant: number;
  email: string;
  name: string;
}

/**
 * Versicherungen erhalten systemseitig nie E-Mails und lassen sich nicht
 * freischalten. Der Dialog zeigt dort den gesperrten Zustand statt eines
 * Schalters; hier wird die Zeile deshalb nie als Mute gemeldet – der Server
 * würde den Aufruf ohnehin mit 409 ablehnen.
 */
export function selectionIsInsurance(selection: Pick<NotifySelection, "type" | "role">): boolean {
  if (!selection) return false;
  if (selection.type === "insurance") return true;
  return isInsuranceParticipant({ roles: selection.role ? [selection.role] : [] });
}

/** Bildet die Auswahlart auf den Schlüssel des Mute-Endpunkts ab. */
export function muteTypeForSelection(type: SelectionKind): ParticipantMuteType {
  switch (type) {
    case "department":
      return "department";
    case "invitation":
      return "invitation";
    case "central":
    case "contact":
    case "insurance":
    default:
      return "external";
  }
}

function isUsableId(id: unknown): id is number {
  return typeof id === "number" && Number.isFinite(id) && id > 0;
}

/**
 * Liefert die Mute-Aufrufe für alle Zeilen, deren Schalter aus ist und die
 * einen eigenen Benachrichtigungskanal haben. Die Eingabe bleibt unverändert.
 */
export function participantMutesFromSelection(created: CreatedParticipant[]): ParticipantMuteRequest[] {
  if (!Array.isArray(created)) return [];

  return created.flatMap((entry) => {
    const selection = entry?.selection;
    if (!selection || selection.notify !== false) return [];
    if (selectionIsInsurance(selection)) return [];
    if (!isUsableId(entry.idParticipant)) return [];

    return [{
      participantType: muteTypeForSelection(selection.type),
      idParticipant: entry.idParticipant,
      email: (selection.email || "").trim(),
      name: (selection.name || "").trim(),
    }];
  });
}

/**
 * POST /registration/start liefert nur das Session-Token, nicht die
 * numerische Session-ID. Die Beteiligten-Liste (/involved) führt ausstehende
 * Einladungen aber mit Status "pending" und `id` = Session-ID. Damit lässt
 * sich die frisch versendete Einladung über die Adresse wiederfinden.
 */
export function findPendingInvitationId(
  involved: unknown,
  email: string,
): number | null {
  const needle = (email || "").trim().toLowerCase();
  if (!needle || !Array.isArray(involved)) return null;

  const match = involved.find((entry) => {
    const candidate = entry as { id?: unknown; status?: unknown; email?: unknown } | null;
    if (!candidate || candidate.status !== "pending") return false;
    return typeof candidate.email === "string" && candidate.email.trim().toLowerCase() === needle;
  }) as { id?: unknown } | undefined;

  const id = Number(match?.id);
  return isUsableId(id) ? id : null;
}
